import React, { useState, useEffect, useRef } from 'react';
import UserInfoContext from '../../utis/userInfoContext';
import apiGETRequest from '../../utis/apiRequest';

// Initialize global window flag for React StrictMode protection
// This persists across component remounts and even page refreshes (until cleared)
if (typeof window !== 'undefined' && window.__USER_INFO_PROVIDER_FETCH_INITIATED__ === undefined) {
  window.__USER_INFO_PROVIDER_FETCH_INITIATED__ = false;
}

const UserInfoProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [userProfileData, setUserProfileData] = useState(null);
  const [userCompanyData, setUserCompanyData] = useState(null);
  const [showNotifications, setShowNotifications] = useState({
    showToast: false,
    toastType: "success",
    toastMessage: "",
  });
  const [showCreateTerms, setShowCreateTerms] = useState(false);
  const [openPrivacyNotice, setOpenPrivacyNotice] = useState(false);
  const isFetchingRef = useRef(false);
  
  // Initialize user data once when component mounts
  useEffect(() => {
    // Only fetch if we don't have user data yet, haven't fetched globally, and not currently fetching
    // This ensures data is fetched once and persists across route changes
    // Check window flag FIRST (synchronously) to prevent React StrictMode double-invocation
    if (userData?.id || isFetchingRef.current || (typeof window !== 'undefined' && window.__USER_INFO_PROVIDER_FETCH_INITIATED__)) {
      return;
    }

    // Set window flag IMMEDIATELY and synchronously before any async operations
    // This prevents React StrictMode's second effect invocation from proceeding
    if (typeof window !== 'undefined') {
      window.__USER_INFO_PROVIDER_FETCH_INITIATED__ = true;
    }
    isFetchingRef.current = true;

    const fetchUserData = async () => {
      try {
        // apiGETRequest has built-in deduplication via pendingRequests Map
        // Even if this effect runs multiple times (React StrictMode), the actual network request
        // will only happen once because apiGETRequest returns the same promise for duplicate calls
        const userInfo = await apiGETRequest('edit.json');
        if (userInfo && !userInfo.error && userInfo.id) {
          setUserData(userInfo);
          
          // Fetch profile data (also has deduplication via apiGETRequest)
          // This will reuse the same promise if called simultaneously
          const profileInfo = await apiGETRequest(`profiles/${userInfo.id}.json`);
          if (profileInfo && !profileInfo.error) {
            setUserProfileData(profileInfo);
            if (profileInfo.privacy_link) {
              window.__userPrivacyLink = profileInfo.privacy_link;
            }
          }
        } else {
          // Reset flag if fetch failed so retry is possible
          if (typeof window !== 'undefined') {
            window.__USER_INFO_PROVIDER_FETCH_INITIATED__ = false;
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        // Reset flag on error so retry is possible if needed
        if (typeof window !== 'undefined') {
          window.__USER_INFO_PROVIDER_FETCH_INITIATED__ = false;
        }
      } finally {
        isFetchingRef.current = false;
      }
    };

    // Fetch data on mount - this will only run once due to the checks above
    // The window flag + apiGETRequest's deduplication provide double protection
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount

  return (
    <UserInfoContext.Provider
      value={{
        userInfoData: userData,
        profileData: userProfileData,
        companyData: userCompanyData,
        showNotifications: showNotifications,
        showCreateTerms: showCreateTerms,
        setShowCreateTerms,
        setUserData,
        setUserProfileData,
        setUserCompanyData,
        setShowNotifications,
        openPrivacyNotice,
        setOpenPrivacyNotice
      }}
    >
      {children}
    </UserInfoContext.Provider>
  );
};

export default UserInfoProvider;

